import IterableDataProcessor from './IterableDataProcessor.js';
import ResultType from './../enums/ResultType.js';
import ResultsFormatterBase from './../formatters/ResultsFormatterBase.js';
import { ResultPair } from '../valueObjects/index.js';
import { ResultData } from '../enums/index.js';

class BaseballResultsProcessor extends IterableDataProcessor {
    constructor(enumValueProvider, converter) {
        super(enumValueProvider, converter);
    }
    
    /**
    * get related pitch or hit from data set
    * @param numeric RelatedResultID - ID of pitch/hit result
    * @param object dataSet - required, array with results
    * @param string resultIdProperty - optional (default ResultID) - node which will be used as a pitch data source
    * @return object - related pitch or false
    */
    static getRelatedResult(RelatedResultID, dataSet, resultIdProperty) {
        if (typeof dataSet !== 'object' || dataSet.hasOwnProperty('length') === false) {
            throw new TypeError('dataSet must be defined as an array');
        }
        if (typeof resultIdProperty === 'undefined') {
            resultIdProperty = ResultData.Id.key;
        }

        let relatedResult = dataSet.find(x => x.hasOwnProperty(resultIdProperty) && x[resultIdProperty] === RelatedResultID);
        if (relatedResult) {
            return relatedResult;
        }

        return false;
    }

    static matchPitchesAndHits(dataSet, relatedHitProperty, relatedPitchProperty, resultIdProperty){
        if (typeof dataSet !== 'object' || dataSet.hasOwnProperty('length') === false) {
            throw new TypeError('dataSet must be defined as an array');
        }

        if (typeof resultIdProperty === 'undefined') {
            resultIdProperty = ResultData.Id.key;
        }

        if (typeof relatedPitchProperty === 'undefined') {
            relatedPitchProperty = ResultData.PitchId.key;
        }

        if (typeof relatedHitProperty === 'undefined') {
            relatedHitProperty = ResultData.HitId.key;
        }

        let matched = [];
        let matchedPitches = [];
        let matchedHits = [];

        // pitch only matching
        for (let i = 0; i < dataSet.length; i++ ){
            let singleResult = dataSet[i];
            if (!singleResult || !singleResult.hasOwnProperty(resultIdProperty) || singleResult[ResultData.Type.key] !== ResultType.Pitch.key){
                continue;
            }

            let hit = null;

            matchedPitches.push(singleResult[resultIdProperty]);

            if (singleResult[relatedHitProperty] > 0){
                const matchedHit = BaseballResultsProcessor.getRelatedResult(singleResult[relatedHitProperty], dataSet, resultIdProperty);
                if (matchedHit){
                    hit = matchedHit;
                    matchedHits.push(matchedHit[resultIdProperty]);
                }
            }

            matched.push(new ResultPair(singleResult, hit));
        }

        for (let i = 0; i < dataSet.length; i++ ){
            let singleResult = dataSet[i];
            if (!singleResult || !singleResult.hasOwnProperty(resultIdProperty) || singleResult[ResultData.Type.key] !== ResultType.Hit.key){
                continue;
            }

            if (matchedHits.indexOf(singleResult[resultIdProperty]) > -1){
                // already added
                continue;
            }

            let pitch = null;

            matchedHits.push(singleResult[resultIdProperty]);

            if (singleResult[relatedPitchProperty] > 0){
                let matchedPitch = BaseballResultsProcessor.getRelatedResult(singleResult[relatedPitchProperty], dataSet, resultIdProperty);
                if (matchedPitch){
                    if (matchedPitches.indexOf(matchedPitch[resultIdProperty]) > -1){
                        // already added, it means that result id matching is incorrect
                        continue;
                    }

                    pitch = matchedPitch;
                    matchedPitches.push(matchedPitch[resultIdProperty]);
                }
            }

            matched.push(new ResultPair(pitch, singleResult));
        }

        return matched;
    }

    static getBatterIdFromResult(result, baseOnTagId){
        let shouldBaseOnTagId = typeof(baseOnTagId) === 'undefined' ? true : !!baseOnTagId;

        if (shouldBaseOnTagId){
            return result.ResultType === ResultType.Pitch.key ? result.RelatedBatterTagID : result.TagID;
        }

        return result.ResultType === ResultType.Pitch.key ? result.RelatedBatterPlayerID : result.PlayerID;
    }

    static applyFormatting(results, ...formatters){
        if (!(results instanceof Array)){
            throw new TypeError(`results must contain an array`);
        }

        if (!(formatters instanceof Array)){
            throw new TypeError(`formatters must contain an array`);
        }

        for (let i = 0; i < formatters.length; i++){
            let formatter = formatters[i];
            if (!(formatter instanceof ResultsFormatterBase)){
                throw new TypeError(`Formatter with index ${i} is not instance of ResultsFormatterBase`);
            }

            results = formatter.format(results);
        }

        return results;
    }

    // proxy for static methods
    matchPitchesAndHits(dataSet, relatedHitProperty, relatedPitchProperty, resultIdProperty){
        return BaseballResultsProcessor.matchPitchesAndHits(dataSet, relatedHitProperty, relatedPitchProperty, resultIdProperty);
    }

    getRelatedResult(RelatedResultID, dataSet, resultIdProperty) {
        return BaseballResultsProcessor.getRelatedResult(RelatedResultID, dataSet, resultIdProperty);
    }

    applyFormatting(results, ...formatters) {
        return BaseballResultsProcessor.applyFormatting(results, ...formatters);
    }

    getBatterIdFromResult(result, baseOnTagId){
        return BaseballResultsProcessor.getBatterIdFromResult(result, baseOnTagId);
    }

    getAllUniqueBatters(dataSet, baseOnTagId) {
        let allBatters = dataSet.map( result => BaseballResultsProcessor.getBatterIdFromResult(result, baseOnTagId));
        return this.getUniqueValues(allBatters, [], false);
    }
}

export default BaseballResultsProcessor;